import { useEffect, useMemo, useState } from 'react';

import { FarIcons } from '@playbooks/icons';
import { SearchField } from '@playbooks/molecules/forms';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';

import { AgentSkillFlow } from 'components/agent/agent-skill-flow';
import {
	ComposerActionFeedback,
	ComposerActionHeader,
	ComposerActionListSkeleton,
	ComposerActionRow,
	ComposerActionSection,
} from 'components/composer/composer-actions-components';
import { useProject, useStore, useWorkspace } from 'contexts';
import { useInit } from 'hooks';
import { FlowBody } from 'utils/flow';
import { projectResourcePayload } from 'utils/project-resources';

const emptySkills = [];

const ComposerActionsSkills = ({ type, disabled, preferences: draftPreferences, onChange, onNext, onPrev }) => {
	const [skills, setSkills] = useState([]);
	const [query, setQuery] = useState('');
	const [flowOpen, setFlowOpen] = useState(false);
	const [flowView, setFlowView] = useState('select');
	const project = useProject();
	const store = useStore();
	const workspace = useWorkspace();
	const draftMode = type === 'app';
	const preferences = draftMode ? draftPreferences || {} : {};
	const selected = draftMode
		? preferences.skills || emptySkills
		: (project.project?.skills || []).map(item => item.skill);
	const rootApi = type === 'project' ? `${project?.rootApi}/resources` : null;
	const compact = useMemo(() => skills.slice(0, 12), [skills]);
	const canManageResources = Boolean(workspace.workspace.access?.capabilities?.canManageProjectDefaults);

	const [fetchSkills, loading, error] = useInit(async () => {
		const response = await store.query({
			url: workspace.workspace?.id ? `${workspace.rootApi}/skills` : '/agent-skills',
			params: {
				view: 'featured',
				pageSize: 12,
				...(query.trim() ? { query: query.trim() } : {}),
			},
		});
		setSkills(response.data || []);
	});

	useEffect(() => {
		const timeout = setTimeout(() => fetchSkills(), query ? 200 : 0);
		return () => clearTimeout(timeout);
	}, [query, store]);

	const onSave = async (values, finish = false) => {
		if (disabled) return;
		if (!draftMode && workspace.workspace?.id) {
			await Promise.all(
				values
					.filter(skill => !skill.isOwner && !skills.some(item => item.id === skill.id))
					.map(skill =>
						store.createRecord({
							url: `${project.rootApi}/resources/skills`,
							data: {
								source: skill.visibility === 'public' ? 'marketplace' : 'workspace',
								resourceId: skill.id,
							},
						}),
					),
			);
		}
		const formatted = values.map((item, position) => ({ ...item, id: item.id, position }));
		if (draftMode) {
			onChange?.({ ...preferences, skills: formatted });
			return;
		}
		const response = await store.updateRecord({
			url: rootApi,
			data: projectResourcePayload(project.project, { skillIds: formatted.map(item => item.id) }),
		});
		project.onUpdate(response.data);
		if (finish) onNext?.();
	};
	const onSelect = skill => {
		const exists = selected.some(item => item.id === skill.id);
		return onSave(exists ? selected.filter(item => item.id !== skill.id) : [...selected, skill]);
	};
	const onFlow = view => {
		setFlowView(view);
		setFlowOpen(true);
	};
	const renderSkill = skill => (
		<ComposerActionRow
			key={skill.id}
			label={skill.name}
			selected={selected.some(item => item.id === skill.id)}
			disabled={disabled}
			trailing={
				<StructuralIcon
					icon={selected.some(item => item.id === skill.id) ? FarIcons.farCircleCheck : FarIcons.farChevronRight}
					className={
						selected.some(item => item.id === skill.id) ? 'size-3.5 text-success' : 'size-3 text-foreground-body'
					}
					aria-hidden
				/>
			}
			onClick={() => onSelect(skill)}
		/>
	);

	return (
		<>
			<ComposerActionHeader onPrev={onPrev}>
				<SearchField
					value={query}
					placeholder='Search skills...'
					className='h-8 grow'
					onChange={event => setQuery(event.target.value)}
					onClear={() => setQuery('')}
				/>
			</ComposerActionHeader>
			<FlowBody>
				{loading ? (
					<ComposerActionListSkeleton count={5} />
				) : error ? (
					<ComposerActionFeedback
						icon='triangle-exclamation'
						title='Skills unavailable'
						text={error?.message || 'Skills could not be loaded.'}
					/>
				) : compact.length ? (
					<ComposerActionSection>{compact.map(renderSkill)}</ComposerActionSection>
				) : (
					<ComposerActionFeedback
						icon='sparkles'
						title={query ? 'No skills found' : 'No featured skills'}
						text={query ? 'Try a different search.' : 'Featured agent skills will appear here.'}
					/>
				)}
				<ComposerActionSection>
					<ComposerActionRow label='View More' disabled={disabled} onClick={() => onFlow('select')} />
					<ComposerActionRow
						label='Add New'
						disabled={disabled || !canManageResources}
						onClick={() => onFlow('form')}
					/>
				</ComposerActionSection>
			</FlowBody>
			<AgentSkillFlow
				open={flowOpen}
				setOpen={setFlowOpen}
				initialView={flowView}
				selected={selected}
				onSave={onSave}
				canManageResources={canManageResources}
				onCreate={
					draftMode
						? async resource => ({
								...resource,
								id: `pending-skill-${Date.now()}`,
								pendingResource: resource,
							})
						: undefined
				}
			/>
		</>
	);
};

export { ComposerActionsSkills };
